import type { Key9Result } from '../types/puzzle';
import { normalizeKey9Result } from '../logic/key9Progress';
import {
  getDailyProgressStorageKey,
  isSameDailyPuzzleIdentity,
  type DailyPuzzleIdentity
} from '../logic/puzzleIdentity';

export const STORAGE_KEY = 'daysudoku:key9:v1';

export type DaySudokuPhase =
  | 'key9_in_progress'
  | 'key9_unlocked'
  | 'full_in_progress'
  | 'full_done'
  | 'missed';

export type DayProgress = {
  phase: DaySudokuPhase;
  identity?: DailyPuzzleIdentity;
  key9Result?: Key9Result;
  fullBoard?: string[];
  fullNotes?: string[];
  fullHints?: number;
  fullSeconds?: number;
};

export type DaySudokuSave = {
  lastDate?: string;
  currentStreak: number;
  bestStreak: number;
  results: Record<string, Key9Result>;
  days: Record<string, DayProgress>;
};

export function loadSave(): DaySudokuSave {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptySave();
    return normalizeSave(JSON.parse(raw) as Partial<DaySudokuSave>);
  } catch {
    return emptySave();
  }
}

export function getSavedDayProgress(state: DaySudokuSave, date: string, identity?: DailyPuzzleIdentity | null): DayProgress | null {
  if (!identity) return state.days[date] ?? null;
  const scoped = state.days[getSaveKey(date, identity)];
  if (isProgressForIdentity(scoped, identity)) return scoped;
  const dateScoped = state.days[date];
  if (isProgressForIdentity(dateScoped, identity)) return dateScoped;
  return null;
}

export function getSavedKey9Result(state: DaySudokuSave, date: string, identity?: DailyPuzzleIdentity | null): Key9Result | null {
  const progress = getSavedDayProgress(state, date, identity);
  if (progress?.key9Result) return progress.key9Result;
  if (!identity) return state.results[date] ?? null;
  if (progress) return state.results[getSaveKey(date, identity)] ?? null;
  return null;
}

export function saveKey9Unlock(date: string, result: Key9Result, identity?: DailyPuzzleIdentity): DaySudokuSave {
  const state = loadSave();
  const key9Result = normalizeKey9Result(result);
  const saveKey = getSaveKey(date, identity);
  const previous = getSavedDayProgress(state, date, identity);
  if (getSavedKey9Result(state, date, identity)?.status === 'missed' || previous?.key9Result?.status === 'missed') {
    return writeSave(state);
  }
  state.results[saveKey] = key9Result;
  state.days[saveKey] = withIdentity({
    ...previous,
    phase: previous?.phase === 'full_done' ? 'full_done' : 'key9_unlocked',
    key9Result
  }, identity);
  return writeSave(state);
}

export function saveKey9Missed(date: string, result: Key9Result, identity?: DailyPuzzleIdentity): DaySudokuSave {
  const state = loadSave();
  const key9Result = normalizeKey9Result(result);
  const saveKey = getSaveKey(date, identity);
  state.results[saveKey] = key9Result;
  state.days[saveKey] = withIdentity({
    phase: 'missed',
    key9Result
  }, identity);
  return writeSave(state);
}

export function saveFullProgress(date: string, fullBoard: string[], fullNotes?: string[], fullHints?: number, identity?: DailyPuzzleIdentity): DaySudokuSave {
  const state = loadSave();
  const saveKey = getSaveKey(date, identity);
  const previous = getSavedDayProgress(state, date, identity);
  state.days[saveKey] = withIdentity({
    ...previous,
    phase: previous?.phase === 'full_done' ? 'full_done' : 'full_in_progress',
    fullBoard: [...fullBoard],
    fullNotes: fullNotes ? [...fullNotes] : previous?.fullNotes,
    fullHints: normalizeFullHints(fullHints ?? previous?.fullHints)
  }, identity);
  return writeSave(state);
}

export function clearActiveFullProgress(date: string, identity?: DailyPuzzleIdentity): DaySudokuSave {
  const state = loadSave();
  const saveKey = getSaveKey(date, identity);
  const previous = getSavedDayProgress(state, date, identity);
  if (!previous) return writeSave(state);
  if (previous.phase === 'full_done') return writeSave(state);
  const key9Result = previous.key9Result ?? getSavedKey9Result(state, date, identity) ?? undefined;

  if (key9Result) {
    state.days[saveKey] = withIdentity({
      phase: key9Result.status === 'missed' ? 'missed' : 'key9_unlocked',
      key9Result
    }, identity);
  } else {
    state.days[saveKey] = withIdentity({
      phase: 'key9_in_progress'
    }, identity);
  }
  return writeSave(state);
}

export function saveFullDone(date: string, fullBoard: string[], seconds: number, fullNotes?: string[], fullHints?: number, identity?: DailyPuzzleIdentity): DaySudokuSave {
  const state = loadSave();
  const saveKey = getSaveKey(date, identity);
  const previous = getSavedDayProgress(state, date, identity);
  if (previous?.phase !== 'full_done') {
    state.currentStreak = calculateNextStreak(state.lastDate, date, state.currentStreak);
    state.bestStreak = Math.max(state.bestStreak, state.currentStreak);
    state.lastDate = date;
  }

  state.days[saveKey] = withIdentity({
    ...previous,
    phase: 'full_done',
    fullBoard: [...fullBoard],
    fullNotes: fullNotes ? [...fullNotes] : previous?.fullNotes,
    fullHints: normalizeFullHints(fullHints ?? previous?.fullHints),
    fullSeconds: seconds
  }, identity);
  return writeSave(state);
}

export function saveResult(date: string, result: Key9Result, identity?: DailyPuzzleIdentity): DaySudokuSave {
  return result.status === 'missed' ? saveKey9Missed(date, result, identity) : saveKey9Unlock(date, result, identity);
}

function emptySave(): DaySudokuSave {
  return { currentStreak: 0, bestStreak: 0, results: {}, days: {} };
}

function normalizeSave(raw: Partial<DaySudokuSave>): DaySudokuSave {
  const results: Record<string, Key9Result> = {};
  Object.entries(raw.results ?? {}).forEach(([date, result]) => {
    if (result) results[date] = normalizeKey9Result(result);
  });

  const save: DaySudokuSave = {
    lastDate: raw.lastDate,
    currentStreak: Number(raw.currentStreak) || 0,
    bestStreak: Number(raw.bestStreak) || 0,
    results,
    days: {}
  };

  Object.entries(raw.days ?? {}).forEach(([date, progress]) => {
    const normalized = normalizeDayProgress(progress as Partial<DayProgress> & Record<string, unknown>);
    if (normalized) save.days[date] = normalized;
  });

  Object.entries(save.results).forEach(([date, result]) => {
    if (save.days[date]) return;
    save.days[date] = {
      phase: result.status === 'missed' ? 'missed' : 'key9_unlocked',
      key9Result: result
    };
  });

  return save;
}

function normalizeDayProgress(raw: (Partial<DayProgress> & Record<string, unknown>) | null | undefined): DayProgress | null {
  if (!raw) return null;
  const hasFullBoard = Array.isArray(raw.fullBoard) && raw.fullBoard.length === 81;
  const rawPhase = isDaySudokuPhase(raw.phase) ? raw.phase : undefined;
  const key9Result = raw.key9Result ? normalizeKey9Result(raw.key9Result) : undefined;
  let phase: DaySudokuPhase = rawPhase ?? (key9Result?.status === 'missed' ? 'missed' : key9Result ? 'key9_unlocked' : 'key9_in_progress');

  if (phase !== 'full_done' && hasFullBoard) {
    phase = 'full_in_progress';
  }

  const normalized: DayProgress = { phase };
  if (isDailyPuzzleIdentity(raw.identity)) normalized.identity = raw.identity;
  if (key9Result) normalized.key9Result = key9Result;
  if (hasFullBoard) normalized.fullBoard = [...raw.fullBoard as string[]];
  if (Array.isArray(raw.fullNotes)) normalized.fullNotes = [...raw.fullNotes];
  if (raw.fullHints != null) normalized.fullHints = normalizeFullHints(raw.fullHints);
  if (raw.fullSeconds != null) normalized.fullSeconds = Math.max(0, Math.floor(Number(raw.fullSeconds) || 0));

  return normalized;
}

function getSaveKey(date: string, identity?: DailyPuzzleIdentity | null): string {
  return identity ? getDailyProgressStorageKey(identity) : date;
}

function isProgressForIdentity(progress: DayProgress | null | undefined, identity: DailyPuzzleIdentity): progress is DayProgress {
  return isSameDailyPuzzleIdentity(progress?.identity, identity);
}

function withIdentity<T extends DayProgress>(progress: T, identity?: DailyPuzzleIdentity): T {
  if (identity) progress.identity = identity;
  return progress;
}

function isDailyPuzzleIdentity(value: unknown): value is DailyPuzzleIdentity {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.dailyDate === 'string'
    && typeof candidate.puzzleId === 'string'
    && typeof candidate.puzzleDate === 'string'
    && typeof candidate.puzzlePath === 'string'
    && typeof candidate.puzzleHash === 'string';
}

function isDaySudokuPhase(value: unknown): value is DaySudokuPhase {
  return value === 'key9_in_progress'
    || value === 'key9_unlocked'
    || value === 'full_in_progress'
    || value === 'full_done'
    || value === 'missed';
}

function normalizeFullHints(value: number | undefined): number {
  return Math.max(0, Math.floor(Number(value) || 0));
}

function writeSave(state: DaySudokuSave): DaySudokuSave {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

function calculateNextStreak(lastDate: string | undefined, today: string, current: number): number {
  if (!lastDate) return 1;
  const last = new Date(`${lastDate}T00:00:00`);
  const now = new Date(`${today}T00:00:00`);
  const diffDays = Math.round((now.getTime() - last.getTime()) / 86400000);
  if (diffDays === 0) return current;
  if (diffDays === 1) return current + 1;
  return 1;
}
